'use client';
import React from 'react';
import { Container, Stack, Typography, Box } from '@mui/material';
import { ComponentSharedVideoBanner, HeaderAlignment } from '@peerless/data-access/strapi';
import { useGetDevice, formatTextPosition, formatPosition } from '../utils';
import { withSectionLayout } from './SectionLayout';

interface Props {
  data: ComponentSharedVideoBanner;
}

const getMinHeight = (height: number | string | undefined): string => {
  if (typeof height === 'string') {
    switch (height.toLowerCase()) {
      case 'full_height':
        return '100vh';
      case 'three_quarters':
        return '75vh';
      case 'half_height':
        return '50vh';
      default:
        return height;
    }
  }
  return height ? `${height}px` : '100vh';
};

const VideoBanner: React.FC<Props> = ({ data }) => {
  const {
    title,
    textColorVB,
    minHeightVB,
    videoBackground,
    titleHorizontalAlign,
    titleVerticalAlign,
    subContent,
  } = data;
  const device = useGetDevice();
  const isMobile = device === 'mobile';

  const rawVideoUrl = videoBackground?.data?.attributes?.url;
  const videoUrl = rawVideoUrl
    ? rawVideoUrl.startsWith('http')
      ? rawVideoUrl
      : `${process.env.NEXT_PUBLIC_STRAPI_API_ROOT || ''}${rawVideoUrl}`
    : undefined;
  const DEFAULT_HEADER_ALIGNMENT: HeaderAlignment = 'Left';
  return (
    <Stack
      sx={{
        width: '100%',
        position: 'relative',
        minHeight: getMinHeight(minHeightVB),
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'end',
        justifyContent: formatPosition(titleVerticalAlign ?? DEFAULT_HEADER_ALIGNMENT),
      }}
    >
      {videoUrl && !isMobile && (
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={videoUrl} type="video/mp4" />
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          backgroundColor:
            textColorVB === 'white' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
        }}
      ></Box>
      <Container
        maxWidth={'xl'}
        sx={{
          position: 'relative',
          zIndex: 1,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: formatPosition(titleHorizontalAlign ?? DEFAULT_HEADER_ALIGNMENT),
          color: textColorVB,
        }}
      >
        {title && (
          <Typography
            variant="h1"
            className="header-underline"
            align={formatTextPosition(titleHorizontalAlign ?? DEFAULT_HEADER_ALIGNMENT)}
          >
            {title}
          </Typography>
        )}
        {subContent && <div dangerouslySetInnerHTML={{ __html: subContent }} />}
      </Container>
    </Stack>
  );
};
export const VideoBannerSection = withSectionLayout(VideoBanner);
